"use client";

import { motion } from "framer-motion";
import { ImageIcon, Pencil, UtensilsCrossed } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { useRecipeFrom } from "@/app/hooks/useRecipeOrigin";
import { useAuthGate } from "@/app/providers/AuthGateProvider";
import { AddToListButton } from "@/components/recipes/AddToListButton";
import { FavoriteButton } from "@/components/recipes/FavoriteButton";
import { MultiplierSlider } from "@/components/recipes/MultiplierSlider";
import { RecipeBackButton } from "@/components/recipes/RecipeBackButton";
import { LinkifiedText } from "@/components/shared/LinkifiedText";
import { buttonVariants } from "@/components/ui/button";
import { FadeImage } from "@/components/ui/fade-image";
import { Link } from "@/i18n/navigation";
import { easeSmooth, fadeSlideUpVariants, staggerContainerVariants } from "@/lib/animations";
import { formatQty, parseIngredient } from "@/lib/recipes/ingredient-parser";
import type {
  RecipeCreator,
  RecipeDetail,
  RecipeDirection,
  RecipeIngredient,
} from "@/lib/recipes/types";
import { cn } from "@/lib/utils";

interface RecipeDetailContentProps {
  recipe: RecipeDetail;
  isOwner: boolean;
  isAuthenticated: boolean;
  initialFavorited: boolean;
}

export function RecipeDetailContent({
  recipe,
  isOwner,
  isAuthenticated,
  initialFavorited,
}: RecipeDetailContentProps) {
  const t = useTranslations("recipeDetail");
  const origin = useRecipeFrom();
  const { requireAuth } = useAuthGate();
  const [multiplier, setMultiplier] = useState(1);

  const parsedIngredients = recipe.ingredients.map((ing) => ({
    ...ing,
    parsed: parseIngredient(ing.text),
  }));

  const scaledIngredientLines = parsedIngredients.map((ing) => {
    const { parsed } = ing;
    if (parsed.qty === null || multiplier === 1) return ing.text;
    const scaledQty = formatQty(parsed.qty, multiplier);
    const parts = [scaledQty];
    if (parsed.unit) parts.push(parsed.unit);
    if (parsed.name) parts.push(parsed.name);
    return parts.join(" ");
  });

  return (
    <article className="mx-auto flex w-full max-w-wide-content flex-1 flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 pt-[calc(env(safe-area-inset-top)+1rem)] md:px-10 md:pt-5">
        <RecipeBackButton className={buttonVariants({ variant: "outline", size: "icon" })} />
        <div className="flex items-center gap-2">
          <FavoriteButton
            recipeId={recipe.id}
            initial={initialFavorited}
            onAuthGate={isAuthenticated ? undefined : requireAuth}
            className={buttonVariants({ variant: "outline", size: "icon" })}
          />
          {isOwner && (
            <Link
              href={`/recipes/edit/${recipe.id}`}
              className={cn(buttonVariants({ variant: "outline", size: "sm" }), "gap-1.5")}
            >
              <Pencil className="size-3" aria-hidden />
              {t("editLink")}
            </Link>
          )}
        </div>
      </div>

      {/* Hero — side-by-side on desktop, photo-first stacked on mobile */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainerVariants(0.1)}
        className="flex flex-col gap-6 px-3 pt-5 md:px-10 md:pt-10 @md:flex-row @sm:gap-8 @lg:gap-14"
      >
        {/* Title block — order-2 on mobile so photo comes first */}
        <motion.div
          variants={fadeSlideUpVariants}
          className="order-2 flex min-w-0 flex-col @md:order-1 @md:flex-1 @md:basis-0"
        >
          <h1 className="text-[32px] font-semibold leading-tight tracking-tight [overflow-wrap:anywhere] @md:text-[48px] @lg:text-[64px] @md:leading-none @md:tracking-[-1.5px]">
            {recipe.title}
          </h1>

          {recipe.creator && !isOwner && recipe.publishedAt && (
            <CreatorAttribution creator={recipe.creator} />
          )}

          {recipe.publishedAt === null && (
            <span className="mt-3 inline-block w-fit rounded bg-amber-500/90 px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.6px] text-amber-950">
              {t("draftBadge")}
            </span>
          )}

          {/* Mobile: compact inline stats */}
          {recipe.servings && (
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground @md:hidden">
              <span className="inline-flex items-center gap-1.5">
                <UtensilsCrossed className="size-3" aria-hidden />
                {recipe.servings}
              </span>
            </div>
          )}

          {/* Desktop: labeled stat rows */}
          {recipe.servings && (
            <div className="hidden @md:mt-7 @md:flex @md:max-w-[420px] @md:flex-col @md:gap-3 @md:border-t @md:border-border @md:pt-5">
              <StatRow
                icon={<UtensilsCrossed className="size-3" aria-hidden />}
                label={t("servings")}
                value={recipe.servings}
              />
            </div>
          )}

          {recipe.tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5 md:mt-5 md:gap-2">
              {recipe.tags.map((tag) => {
                const dest = origin === "recipes" ? "/recipes" : "/discover";
                const encoded = encodeURIComponent(tag);
                return (
                  <Link
                    key={tag}
                    href={`${dest}?tags=${encoded}`}
                    onClick={() => {
                      fetch("/api/tags/log", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ tagName: tag, eventType: "chip_tap" }),
                      }).catch(() => {});
                    }}
                    className={cn(
                      buttonVariants({ variant: "outline", size: "auto" }),
                      "rounded-full px-2.5 py-0.5 text-[13px] text-muted-foreground"
                    )}
                  >
                    {tag}
                  </Link>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* Photo — order-1 on mobile so it appears first */}
        <motion.div
          variants={fadeSlideUpVariants}
          className="order-1 min-w-0 @md:order-2 @md:flex-1 @md:basis-0 @md:max-w-[580px]"
        >
          <HeroPhoto recipe={recipe} />
        </motion.div>
      </motion.div>

      {/* Two-column body: ingredients (sticky) + directions */}
      <div className="flex flex-col gap-8 px-3 pb-16 pt-8 md:px-10 md:pt-12 @md:grid @md:grid-cols-[280px_1fr] @md:gap-14 @lg:grid-cols-[360px_1fr]">
        {/* Ingredient panel */}
        <div className="@md:sticky @md:top-6 @md:self-start">
          <IngredientPanel
            ingredients={parsedIngredients}
            scaledLines={scaledIngredientLines}
            multiplier={multiplier}
            onMultiplierChange={setMultiplier}
            recipeTitle={recipe.title}
            isAuthenticated={isAuthenticated}
          />
        </div>

        {/* Directions + Notes */}
        <div className="min-w-0">
          {recipe.directions.length > 0 && (
            <section>
              <h2 className="mb-6 text-xs font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                {t("directions")}
              </h2>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainerVariants(0.08)}
                className="flex flex-col"
              >
                {recipe.directions.map((d, i) => (
                  <DirectionStep
                    key={i}
                    direction={d}
                    index={i}
                    isLast={i === recipe.directions.length - 1}
                  />
                ))}
              </motion.div>
            </section>
          )}

          {recipe.notes && (
            <aside className="mt-12">
              <h3 className="mb-6 text-xs font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                {t("notes")}
              </h3>
              <p className="whitespace-pre-wrap leading-relaxed text-muted-foreground">
                <LinkifiedText text={recipe.notes} />
              </p>
            </aside>
          )}
        </div>
      </div>
    </article>
  );
}

function HeroPhoto({ recipe }: { recipe: RecipeDetail }) {
  // A dead image URL shows the no-photo placeholder, not the browser's broken-image icon.
  const [imageFailed, setImageFailed] = useState(false);
  const aspect = recipe.imageAspectRatio;
  const ratioClass =
    aspect && aspect < 0.9
      ? "aspect-[4/5]"
      : aspect && aspect > 1.1
        ? "aspect-[3/2]"
        : "aspect-square";

  return (
    <div
      className={cn("relative w-full overflow-hidden rounded-2xl md:rounded-[18px]", ratioClass)}
    >
      {recipe.image && !imageFailed ? (
        <FadeImage
          src={recipe.image}
          alt={recipe.title}
          fill
          sizes="(min-width: 1280px) 460px, (min-width: 1024px) 50vw, 100vw"
          className="object-cover"
          priority
          onError={() => setImageFailed(true)}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground">
          <ImageIcon className="size-24 opacity-30" strokeWidth={1.5} aria-hidden />
        </div>
      )}
    </div>
  );
}

function StatRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-3.5">
      <span className="hidden items-center gap-2 text-xs font-semibold uppercase tracking-[0.6px] text-muted-foreground @md:inline-flex @md:w-[120px]">
        {icon} {label}
      </span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

interface IngredientPanelProps {
  ingredients: Array<RecipeIngredient & { parsed: ReturnType<typeof parseIngredient> }>;
  scaledLines: string[];
  multiplier: number;
  onMultiplierChange: (v: number) => void;
  recipeTitle: string;
  isAuthenticated: boolean;
}

type ParsedIngredient = RecipeIngredient & { parsed: ReturnType<typeof parseIngredient> };

interface IngredientSection {
  name: string;
  items: ParsedIngredient[];
}

function groupIngredientsBySection(ingredients: ParsedIngredient[]): IngredientSection[] {
  const hasGroups = ingredients.some((i) => i.section);
  if (!hasGroups) return [{ name: "", items: ingredients }];

  const sections: IngredientSection[] = [];
  const map = new Map<string, ParsedIngredient[]>();
  const order: string[] = [];
  for (const ing of ingredients) {
    const key = ing.section ?? "";
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(ing);
  }
  for (const name of order) {
    sections.push({ name, items: map.get(name)! });
  }
  return sections;
}

function IngredientPanel({
  ingredients,
  scaledLines,
  multiplier,
  onMultiplierChange,
  recipeTitle,
  isAuthenticated,
}: IngredientPanelProps) {
  const t = useTranslations("recipeDetail");
  const { requireAuth } = useAuthGate();
  const sections = groupIngredientsBySection(ingredients);
  const hasMultipleSections = sections.length > 1 || (sections.length === 1 && !!sections[0].name);

  const totalItems = ingredients.length;

  return (
    <div className="overflow-hidden rounded-2xl border border-input-border bg-input-bg p-4 md:p-[22px]">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-[20px] font-semibold tracking-tight">{t("ingredientsTitle")}</h2>
        {hasMultipleSections && totalItems > 0 && (
          <span className="text-sm text-muted-foreground">
            {t("itemCount", { count: totalItems })}
          </span>
        )}
      </div>

      <MultiplierSlider value={multiplier} onChange={onMultiplierChange} />

      <div className="my-4 h-px bg-border" />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainerVariants(0.03)}
        className="flex flex-col"
      >
        {sections.map((section, si) => (
          <div key={si}>
            {hasMultipleSections && section.name && (
              <motion.div
                variants={fadeSlideUpVariants}
                className={cn(
                  "flex items-center gap-2 pb-2 pt-3 text-xs font-semibold uppercase tracking-[0.6px] text-muted-foreground",
                  si > 0 && "mt-4"
                )}
              >
                <span className="size-2 shrink-0 rounded-full bg-primary" />
                {section.name}
              </motion.div>
            )}
            {section.items.map((ing, i) => {
              const { parsed } = ing;
              const isLastInSection = i === section.items.length - 1;

              return (
                <motion.div
                  key={`${si}-${i}`}
                  variants={fadeSlideUpVariants}
                  className={cn(
                    "grid items-baseline gap-3 py-2.5",
                    !isLastInSection && "border-b border-border"
                  )}
                  style={{
                    gridTemplateColumns: "68px 1fr auto",
                  }}
                >
                  {parsed.qty !== null ? (
                    <>
                      <span className="font-medium tabular-nums">
                        <motion.span
                          key={`${parsed.qty}-${multiplier}`}
                          initial={{ opacity: 0.5 }}
                          animate={{ opacity: 1 }}
                          transition={easeSmooth}
                        >
                          {formatQty(parsed.qty, multiplier)}
                        </motion.span>
                        {parsed.unit && (
                          <span className="ms-1 text-muted-foreground">{parsed.unit}</span>
                        )}
                      </span>
                      <span className="leading-snug text-muted-foreground">{parsed.name}</span>
                      <span />
                    </>
                  ) : (
                    <>
                      <span className="text-muted-foreground">—</span>
                      <span className="col-span-2 leading-snug text-muted-foreground">
                        {ing.text}
                      </span>
                    </>
                  )}
                </motion.div>
              );
            })}
          </div>
        ))}
      </motion.div>

      {ingredients.length > 0 && (
        <div className="mt-4">
          <AddToListButton
            recipeTitle={recipeTitle}
            ingredientLines={scaledLines}
            onAuthGate={isAuthenticated ? undefined : requireAuth}
            showCount={hasMultipleSections}
            className="h-[42px] w-full rounded-[10px] bg-primary text-base font-semibold text-primary-foreground shadow-[0_1px_0_rgba(255,255,255,0.08)_inset,0_8px_20px_rgba(226,92,74,0.22)] hover:bg-primary/90"
          />
        </div>
      )}
    </div>
  );
}

function DirectionStep({
  direction,
  index,
  isLast,
}: {
  direction: RecipeDirection;
  index: number;
  isLast: boolean;
}) {
  const t = useTranslations("recipeDetail");
  const stepNum = String(index + 1).padStart(2, "0");
  const images = direction.images?.filter(Boolean) ?? [];

  return (
    <motion.div
      variants={fadeSlideUpVariants}
      className={cn(
        "grid grid-cols-[40px_minmax(0,1fr)] gap-4 py-5 md:grid-cols-[56px_minmax(0,1fr)] md:gap-6 md:py-6",
        !isLast && "border-b border-border"
      )}
    >
      {/* As tall as the step's first line, with the number centered in it: a one-line step is centered on its number, a
          longer one starts level with it (Jeremy, 2026-10-10). */}
      <div className="flex h-[1.625rem] items-center text-[28px] font-light leading-none tracking-tight text-muted-foreground md:h-[1.7rem] md:text-[32px]">
        {stepNum}
      </div>
      <div>
        <p className="whitespace-pre-wrap leading-relaxed text-foreground md:leading-[1.7]">
          <LinkifiedText text={direction.text} />
        </p>
        {images.length > 0 && (
          <div
            className={cn(
              "mt-3 grid gap-2",
              images.length === 1 && "max-w-64 grid-cols-1",
              images.length === 2 && "max-w-sm grid-cols-2",
              images.length >= 3 && "grid-cols-3"
            )}
          >
            {images.map((url, imgIdx) => (
              <div
                key={url}
                className="relative aspect-square overflow-hidden rounded-lg border border-border"
              >
                <FadeImage
                  src={url}
                  alt={t("directionImageAlt", {
                    number: index + 1,
                    index: imgIdx + 1,
                  })}
                  fill
                  sizes={images.length === 1 ? "256px" : images.length === 2 ? "192px" : "33vw"}
                  className="object-cover"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function CreatorAttribution({ creator }: { creator: RecipeCreator }) {
  const t = useTranslations("recipeDetail");

  return (
    <p className="mt-2 text-sm text-muted-foreground">
      {t("createdBy")}{" "}
      <Link
        href={`/creators/${creator.username}`}
        className="text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
      >
        @{creator.username}
      </Link>
    </p>
  );
}
